// GitHub Contents API as the shared substrate for a channel.
//
// The channel's session JSON lives at a path in a repo. Each browser
// polls it every ~10s and pushes its own appends back. Concurrent
// edits are resolved by merging the message logs by id+timestamp —
// append-only logs are naturally CRDT-shaped (union of UUID'd
// messages, sorted by created_at).
//
// Owner-mutable fields (title, provider_preference, encrypted_keys,
// sync_target) follow last-write-wins. They change rarely so the
// occasional clobber is acceptable.
//
// No server. The browser is the agent; GitHub is the substrate.

import { parseSession, serializeSession, type Session } from "./session";

const API_BASE = "https://api.github.com";

export interface SyncTarget {
  owner: string;
  repo: string;
  path: string;
  /** Most recent sha we've seen for this file — used for conditional
   *  reads and for PUT optimistic-locking. */
  last_sha?: string;
}

export interface FetchResult {
  kind: "unchanged" | "updated" | "absent";
  session?: Session;
  sha?: string;
}

/** GET the channel's current state from GitHub. 304 (unchanged) is
 *  inferred from sha comparison rather than HTTP semantics — the
 *  Contents API doesn't honor If-None-Match reliably. */
export async function fetchChannel(
  target: SyncTarget,
  token: string,
): Promise<FetchResult> {
  const url = `${API_BASE}/repos/${target.owner}/${target.repo}/contents/${encodeURIComponent(target.path).replace(/%2F/g, "/")}`;
  const resp = await fetch(url, {
    headers: ghHeaders(token),
  });

  if (resp.status === 404) return { kind: "absent" };
  if (!resp.ok) throw await ghError(resp, "fetch");

  type ContentsResponse = {
    sha: string;
    content: string;
    encoding: string;
  };
  const json = (await resp.json()) as ContentsResponse;
  if (target.last_sha && json.sha === target.last_sha) {
    return { kind: "unchanged", sha: json.sha };
  }

  // GitHub returns base64 with embedded newlines every ~60 chars.
  const text = decodeBase64(json.content.replace(/\s+/g, ""));
  const session = parseSession(text);
  return { kind: "updated", session, sha: json.sha };
}

/** PUT the channel state to GitHub. On stale-sha 409, falls back to a
 *  fetch+merge+retry (capped at 2 retries). */
export async function pushChannel(
  target: SyncTarget,
  session: Session,
  token: string,
  message?: string,
): Promise<{ sha: string; session: Session }> {
  let attempt = 0;
  let current = session;
  let sha = target.last_sha;

  while (attempt < 3) {
    attempt++;
    const url = `${API_BASE}/repos/${target.owner}/${target.repo}/contents/${encodeURIComponent(target.path).replace(/%2F/g, "/")}`;
    const body: Record<string, unknown> = {
      message: message ?? `playground: update ${current.title}`,
      content: encodeBase64(serializeSession(current)),
    };
    if (sha) body.sha = sha;

    const resp = await fetch(url, {
      method: "PUT",
      headers: ghHeaders(token),
      body: JSON.stringify(body),
    });

    if (resp.ok) {
      type PutResponse = { content: { sha: string } };
      const json = (await resp.json()) as PutResponse;
      return { sha: json.content.sha, session: current };
    }

    // 409 = sha mismatch (someone else pushed first). Merge and retry.
    // 422 = sometimes returned by GitHub for the same condition.
    if (resp.status === 409 || resp.status === 422) {
      const remote = await fetchChannel({ ...target, last_sha: undefined }, token);
      if (remote.kind === "updated" && remote.session) {
        current = mergeSessions(current, remote.session);
        sha = remote.sha;
        continue;
      }
      // Fell through (absent or unchanged but still 409) — retry without sha.
      sha = undefined;
      continue;
    }

    throw await ghError(resp, "push");
  }
  throw new Error("GitHub sync: gave up after 3 push attempts (high contention).");
}

/** First-time publish: PUT a brand-new file, returns its sha. */
export async function publishChannel(
  target: SyncTarget,
  session: Session,
  token: string,
): Promise<{ sha: string }> {
  const result = await pushChannel({ ...target, last_sha: undefined }, session, token,
    `playground: publish "${session.title}"`);
  return { sha: result.sha };
}

/** Merge two versions of the same session. Messages are unioned by id
 *  and sorted by created_at (append-only CRDT). Owner-mutable metadata
 *  follows last-write-wins by updated_at. */
export function mergeSessions(a: Session, b: Session): Session {
  const seen = new Set<string>();
  const merged = [...a.messages, ...b.messages].filter((m) => {
    if (seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
  merged.sort((x, y) => x.created_at.localeCompare(y.created_at));

  // For metadata, prefer whichever side was updated more recently.
  const winner = a.updated_at > b.updated_at ? a : b;
  return {
    ...winner,
    messages: merged,
    updated_at: new Date().toISOString(),
  };
}

/** Light validation — surfaces a useful error before the user wastes
 *  a passphrase on a typo'd repo. The `not_found` discriminant lets
 *  the UI offer "Create this repo?" inline. */
export type AccessResult =
  | { ok: true }
  | { ok: false; kind: "not_found"; reason: string }
  | { ok: false; kind: "denied"; reason: string }
  | { ok: false; kind: "other"; reason: string };

export async function verifyAccess(
  target: Pick<SyncTarget, "owner" | "repo">,
  token: string,
): Promise<AccessResult> {
  const url = `${API_BASE}/repos/${target.owner}/${target.repo}`;
  const resp = await fetch(url, { headers: ghHeaders(token) });
  if (resp.ok) return { ok: true };
  if (resp.status === 404) return { ok: false, kind: "not_found", reason: "repo doesn't exist (or your PAT can't see it)" };
  if (resp.status === 401) return { ok: false, kind: "denied", reason: "PAT rejected — check the token" };
  if (resp.status === 403) return { ok: false, kind: "denied", reason: "PAT lacks permission (needs `repo` scope for private)" };
  return { ok: false, kind: "other", reason: `GitHub ${resp.status} ${resp.statusText}` };
}

/** Authenticated user — used to decide whether to create the repo
 *  under the user account or under an org. */
export async function whoami(token: string): Promise<{ login: string }> {
  const resp = await fetch(`${API_BASE}/user`, { headers: ghHeaders(token) });
  if (!resp.ok) throw await ghError(resp, "whoami");
  type UserResponse = { login: string };
  const json = (await resp.json()) as UserResponse;
  return { login: json.login };
}

/** Create a private repo at the requested owner/name. Picks the
 *  endpoint based on whether `owner` matches the authenticated user —
 *  if it does, POST /user/repos; otherwise try /orgs/{owner}/repos. */
export async function createRepo(
  target: Pick<SyncTarget, "owner" | "repo">,
  token: string,
): Promise<void> {
  const me = await whoami(token);
  const sameUser = me.login.toLowerCase() === target.owner.toLowerCase();
  const url = sameUser
    ? `${API_BASE}/user/repos`
    : `${API_BASE}/orgs/${target.owner}/repos`;

  const resp = await fetch(url, {
    method: "POST",
    headers: ghHeaders(token),
    body: JSON.stringify({
      name: target.repo,
      private: true,
      auto_init: true,
      description: "BabyAI Playground — synced channel storage. Each JSON file is a channel.",
    }),
  });
  if (!resp.ok) throw await ghError(resp, "create-repo");
}

function ghHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
    "Content-Type": "application/json",
  };
}

async function ghError(resp: Response, op: string): Promise<Error> {
  let body = "";
  try { body = (await resp.text()).slice(0, 200); } catch { /* ignore */ }
  return new Error(`GitHub ${op} ${resp.status} ${resp.statusText}${body ? ` — ${body}` : ""}`);
}

function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function decodeBase64(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
